// src/pages/users/jeepneys/Jeepneys.js
import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  Container,
  Row,
  Col,
  Table,
  Button,
  Modal,
  Form,
  Spinner,
  InputGroup,
  FormControl,
  Pagination,
  Image,
  Tabs,
  Tab,
} from "react-bootstrap";
import api from "../../../services/api";
import { Colors } from "../../../constants/Colors";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";

/* ================================================================== */
/* CONSTANTS                                                          */
/* ================================================================== */

const MySwal = withReactContent(Swal);
const PER_PAGE = 5;

/* ---------------- Design tokens ---------------- */
const T = {
  radius: 14,
  radiusSm: 10,
  shadow: "0 1px 2px rgba(16,24,40,.04), 0 4px 16px rgba(16,24,40,.06)",
  shadowHover: "0 4px 8px rgba(16,24,40,.06), 0 12px 28px rgba(16,24,40,.10)",
  border: `1px solid ${Colors.BORDER_GRAY}`,
  font: "'Inter', 'Segoe UI', system-ui, sans-serif",
};

/* ---------------- Status metadata ---------------- */
const STATUS_COLORS = {
  Active: { color: Colors.SUCCESS, bg: `${Colors.SUCCESS}1f` },
  Inactive: { color: Colors.RED, bg: `${Colors.RED}1f` },
  Maintenance: { color: Colors.WARNING, bg: `${Colors.WARNING}1f` },
};

const DOC_STATUS_META = {
  success: { color: Colors.SUCCESS, bg: `${Colors.SUCCESS}1f` },
  warning: { color: Colors.WARNING, bg: `${Colors.WARNING}1f` },
  danger: { color: Colors.RED, bg: `${Colors.RED}1f` },
  info: { color: Colors.INFO, bg: `${Colors.INFO}1f` },
  secondary: { color: Colors.GRAY, bg: `${Colors.GRAY}26` },
};

const DOC_STATUS_LABELS = {
  valid: "Valid",
  expiring_soon: (d) => `Expires in ${d}d`,
  expired: "Expired",
  pending: "Pending",
};

/* ---------------- Empty form (used on open) ---------------- */
const EMPTY_FORM = {
  jeepney_id: null,
  license_plate: "",
  operator_id: "",
  vehicle_type: "",
  capacity: "",
  status: "",
  franchise_status: "",
  base_location: "",
  has_top_load: false,
  has_door_type: false,
  tags: [],
  image: null,
  image_url: null,
  operator: null,
  documents: [],
  is_disabled: false,
  disabled_reason: null,
  disabled_at: null,
};

/* ================================================================== */
/* HELPERS                                                            */
/* ================================================================== */

const normalizeStatus = (s) =>
  typeof s === "string" && s
    ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase()
    : "—";

const getStatusMeta = (status) => {
  const key = normalizeStatus(status);
  return (
    STATUS_COLORS[key] || {
      color: Colors.GRAY,
      bg: `${Colors.GRAY}26`,
    }
  );
};

const getDocBadgeMeta = (doc) =>
  DOC_STATUS_META[doc?.status_color] || DOC_STATUS_META.secondary;

const getDocBadgeLabel = (doc) => {
  if (!doc) return "Unknown";
  if (doc.status === "expiring_soon" && doc.days_until_expiry != null) {
    return DOC_STATUS_LABELS.expiring_soon(doc.days_until_expiry);
  }
  return DOC_STATUS_LABELS[doc.status] || doc.status || "Unknown";
};

/* Normalize API list response into { rows, page info } */
const parseListResponse = (data) => {
  let rows = [];
  let meta = { total: 0, last_page: 1 };

  if (data?.success && data?.data) {
    if (Array.isArray(data.data.data)) {
      rows = data.data.data;
      meta = {
        total: data.data.total || 0,
        last_page: data.data.last_page || 1,
      };
    } else if (Array.isArray(data.data)) {
      rows = data.data;
      meta = {
        total: rows.length,
        last_page: Math.ceil(rows.length / PER_PAGE),
      };
    }
  } else if (Array.isArray(data)) {
    rows = data;
    meta = { total: rows.length, last_page: Math.ceil(rows.length / PER_PAGE) };
  }

  return { rows, ...meta };
};

/* Normalize a single jeepney response */
const parseDetailResponse = (data) => {
  if (data?.success && data?.data) return data.data;
  if (data?.jeepney_id) return data;
  return null;
};

/* Normalize operators list response */
const parseOperatorsList = (data) => {
  if (data?.success && data?.data) {
    return Array.isArray(data.data) ? data.data : data.data.data || [];
  }
  return Array.isArray(data) ? data : [];
};

/* ================================================================== */
/* STYLES                                                             */
/* ================================================================== */

const Styles = () => (
  <style>{`
    /* ---- Page shell ---- */
    .bk-page { font-family: ${T.font}; background: ${Colors.LIGHT_GRAY}; min-height: 100vh; padding: 24px 0 60px; }
    .bk-page .container-fluid { max-width: 1400px; }

    /* ---- Header ---- */
    .bk-header { background: linear-gradient(135deg, ${Colors.GRADIENT_START} 0%, ${Colors.GRADIENT_END} 100%); color: #fff; border-radius: ${T.radius}px; padding: 26px 30px; margin-bottom: 20px; position: relative; overflow: hidden; box-shadow: 0 10px 30px rgba(14,107,248,.22); }
    .bk-header::after { content: ""; position: absolute; top: -60px; right: -60px; width: 220px; height: 220px; background: radial-gradient(circle, rgba(255,255,255,.18) 0%, transparent 70%); border-radius: 50%; }
    .bk-header h2 { font-weight: 700; letter-spacing: -.02em; margin: 0; font-size: 22px; }
    .bk-header p { opacity: .88; margin: 4px 0 0; font-size: 13.5px; }

    /* ---- Card ---- */
    .bk-card { background: #fff; border-radius: ${T.radius}px; border: ${T.border}; box-shadow: ${T.shadow}; overflow: hidden; }
    .bk-card-header { padding: 16px 22px; border-bottom: ${T.border}; display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 10px; background: #fff; }
    .bk-card-header h5 { margin: 0; font-weight: 600; color: ${Colors.TEXT_DARK}; font-size: 15.5px; display: flex; align-items: center; gap: 8px; }
    .bk-card-header small { font-size: 12.5px; color: ${Colors.TEXT_LIGHT}; }

    /* ---- Toolbar ---- */
    .bk-toolbar { background: #fff; border-radius: ${T.radius}px; border: ${T.border}; padding: 12px; box-shadow: ${T.shadow}; margin-bottom: 18px; }
    .bk-search { border: 1px solid transparent !important; background: ${Colors.LIGHT_GRAY} !important; border-radius: ${T.radiusSm} !important; padding: 9px 13px !important; font-size: 13.5px; transition: all .18s; height: auto !important; }
    .bk-search:focus { background: #fff !important; border-color: ${Colors.PRIMARY} !important; box-shadow: 0 0 0 3px ${Colors.PRIMARY}22 !important; }
    .bk-search-icon { background: ${Colors.LIGHT_GRAY} !important; border: 1px solid transparent !important; border-right: none !important; border-radius: ${T.radiusSm} 0 0 ${T.radiusSm} !important; color: ${Colors.GRAY}; }
    .bk-select { background: ${Colors.LIGHT_GRAY} !important; border: 1px solid transparent !important; border-radius: ${T.radiusSm} !important; padding: 9px 13px !important; font-size: 13.5px; font-weight: 500; color: ${Colors.TEXT_DARK}; height: auto !important; }
    .bk-select:focus { background: #fff !important; border-color: ${Colors.PRIMARY} !important; box-shadow: 0 0 0 3px ${Colors.PRIMARY}22 !important; }

    /* ---- Table ---- */
    .bk-table { margin: 0; }
    .bk-table thead th { background: ${Colors.LIGHT_GRAY}; border: none; font-size: 11px; text-transform: uppercase; letter-spacing: .06em; color: ${Colors.TEXT_LIGHT}; font-weight: 700; padding: 13px 18px; white-space: nowrap; }
    .bk-table tbody td { padding: 15px 18px; border-color: ${Colors.BORDER_GRAY}; vertical-align: middle; font-size: 13.5px; color: ${Colors.TEXT_DARK}; }
    .bk-table tbody tr { transition: background .15s; }
    .bk-table tbody tr:hover { background: ${Colors.LIGHT_GRAY}55; }

    /* ---- Avatars & chips ---- */
    .bk-id-chip { font-family: ui-monospace, "SF Mono", Menlo, monospace; font-size: 11px; font-weight: 600; background: ${Colors.LIGHT_GRAY}; color: ${Colors.TEXT_LIGHT}; padding: 3px 7px; border-radius: 5px; }

    /* ---- Pills ---- */
    .bk-pill { display: inline-flex; align-items: center; gap: 6px; padding: 4px 11px; border-radius: 999px; font-size: 11.5px; font-weight: 600; line-height: 1.4; }
    .bk-pill::before { content: ""; width: 6px; height: 6px; border-radius: 50%; background: currentColor; }

    /* ---- Empty state ---- */
    .bk-empty { text-align: center; padding: 60px 20px; }
    .bk-empty-icon { width: 80px; height: 80px; margin: 0 auto 18px; border-radius: 50%; background: ${Colors.LIGHT_GRAY}; display: grid; place-items: center; font-size: 34px; color: ${Colors.GRAY}; }
    .bk-empty h5 { font-weight: 600; margin-bottom: 6px; }

    /* ---- Skeleton ---- */
    .bk-skel { background: linear-gradient(90deg, ${Colors.LIGHT_GRAY} 25%, #eee 50%, ${Colors.LIGHT_GRAY} 75%); background-size: 200% 100%; animation: bk-shimmer 1.4s infinite; border-radius: 6px; }
    @keyframes bk-shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }

    /* ---- Buttons ---- */
    .bk-btn-primary { background: ${Colors.PRIMARY} !important; border: none !important; color: #fff !important; border-radius: ${T.radiusSm} !important; font-weight: 600; padding: 8px 16px; font-size: 13.5px; transition: all .18s; }
    .bk-btn-primary:hover { background: #e87a2e !important; transform: translateY(-1px); box-shadow: 0 6px 16px ${Colors.PRIMARY}44; }
    .bk-btn-ghost { background: transparent !important; border: 1px solid ${Colors.BORDER_GRAY} !important; color: ${Colors.TEXT_DARK} !important; border-radius: ${T.radiusSm} !important; font-weight: 500; padding: 7px 13px; font-size: 13px; transition: all .18s; }
    .bk-btn-ghost:hover { border-color: ${Colors.PRIMARY} !important; color: ${Colors.PRIMARY} !important; background: ${Colors.PRIMARY}0d !important; }

    /* ---- Info sections ---- */
    .bk-section { background: ${Colors.LIGHT_GRAY}; border-radius: ${T.radiusSm}; padding: 16px; margin-bottom: 14px; }
    .bk-section-title { font-size: 11.5px; font-weight: 700; text-transform: uppercase; letter-spacing: .06em; color: ${Colors.TEXT_LIGHT}; margin-bottom: 12px; display: flex; align-items: center; gap: 7px; }
    .bk-section-title i { color: ${Colors.PRIMARY}; font-size: 14px; }
    .bk-info-row { display: flex; justify-content: space-between; padding: 8px 0; border-bottom: 1px dashed ${Colors.BORDER_GRAY}; font-size: 13px; gap: 12px; }
    .bk-info-row:last-child { border-bottom: none; }
    .bk-info-label { color: ${Colors.TEXT_LIGHT}; flex-shrink: 0; }
    .bk-info-value { color: ${Colors.TEXT_DARK}; font-weight: 600; text-align: right; max-width: 65%; word-break: break-word; }

    /* ---- Modal ---- */
    .modal-content { border-radius: ${T.radius}px; border: none; box-shadow: 0 24px 60px rgba(16,24,40,.18); }
    .modal-header { border-bottom: ${T.border}; padding: 18px 22px; }
    .modal-title { font-weight: 700; color: ${Colors.TEXT_DARK}; font-size: 16px; }
    .modal-body { padding: 20px 22px; }
    .modal-footer { border-top: ${T.border}; padding: 14px 22px; }

    /* ---- Pagination ---- */
    .page-item.active .page-link { background: ${Colors.PRIMARY} !important; border-color: ${Colors.PRIMARY} !important; color: #fff !important; }
    .page-link { color: ${Colors.TEXT_DARK} !important; border-color: ${Colors.BORDER_GRAY} !important; }
    .page-link:hover { background: ${Colors.LIGHT_GRAY} !important; }

    /* ---- Tabs ---- */
    .nav-tabs { border-bottom: ${T.border}; gap: 4px; }
    .nav-tabs .nav-link { border: none !important; color: ${Colors.TEXT_LIGHT}; font-weight: 500; font-size: 13.5px; padding: 9px 15px; border-radius: ${T.radiusSm} ${T.radiusSm} 0 0; }
    .nav-tabs .nav-link:hover { color: ${Colors.TEXT_DARK}; background: ${Colors.LIGHT_GRAY}; }
    .nav-tabs .nav-link.active { color: ${Colors.PRIMARY} !important; background: #fff !important; border-bottom: 2px solid ${Colors.PRIMARY} !important; }

    /* ---- Document thumbnails & preview ---- */
    .doc-thumb { width: 60px; height: 40px; object-fit: cover; border-radius: 6px; border: 1px solid ${Colors.BORDER_GRAY}; cursor: pointer; transition: transform .15s, box-shadow .15s; }
    .doc-thumb:hover { transform: scale(1.06); box-shadow: 0 4px 12px rgba(0,0,0,.15); }
    .bk-no-scan { display: inline-block; padding: 4px 10px; border-radius: 6px; background: ${Colors.LIGHT_GRAY}; color: ${Colors.TEXT_LIGHT}; font-size: 10px; font-weight: 700; letter-spacing: .05em; border: 1px dashed ${Colors.BORDER_GRAY}; }

    /* ---- Document preview modal ---- */
    .doc-preview-body { background: #0e0e0e !important; display: flex; flex-direction: column; align-items: center; justify-content: center; }
    .doc-preview-img { max-width: 100%; max-height: 70vh; border-radius: 8px; box-shadow: 0 4px 20px rgba(0,0,0,.4); }

    /* ---- Disabled banner & pill ---- */
    .bk-disabled-banner { display: flex; gap: 12px; align-items: flex-start; background: ${Colors.RED}12; border: 1.5px solid ${Colors.RED}; border-radius: ${T.radiusSm}px; padding: 14px 16px; margin-bottom: 16px; }
    .bk-disabled-banner-icon { width: 36px; height: 36px; border-radius: 50%; background: ${Colors.RED}22; color: ${Colors.RED}; display: grid; place-items: center; flex-shrink: 0; font-size: 18px; }
    .bk-disabled-banner-title { color: ${Colors.RED}; font-weight: 700; font-size: 14px; margin-bottom: 2px; }
    .bk-disabled-banner-text { color: ${Colors.TEXT_DARK}; font-size: 13px; line-height: 1.4; margin: 0; }
    .bk-disabled-pill { display: inline-flex; align-items: center; gap: 6px; padding: 5px 12px; border-radius: 999px; font-size: 11.5px; font-weight: 600; color: ${Colors.RED}; background: ${Colors.RED}1f; line-height: 1.4; }
    .bk-disabled-pill::before { content: ""; width: 6px; height: 6px; border-radius: 50%; background: currentColor; }
  `}</style>
);

/* ================================================================== */
/* PRESENTATIONAL COMPONENTS                                          */
/* ================================================================== */

/* ---------------- Status pill (respects disabled override) -------- */
const StatusPill = ({ status, isDisabled }) => {
  if (isDisabled) return <span className="bk-disabled-pill">Disabled</span>;
  const m = getStatusMeta(status);
  return (
    <span className="bk-pill" style={{ color: m.color, background: m.bg }}>
      {status || "—"}
    </span>
  );
};

/* ---------------- Document badge ---------------- */
const DocumentBadge = ({ doc }) => {
  const m = getDocBadgeMeta(doc);
  return (
    <span className="bk-pill" style={{ color: m.color, background: m.bg }}>
      {getDocBadgeLabel(doc)}
    </span>
  );
};

/* ---------------- Franchise pill ---------------- */
const FranchisePill = ({ value }) => {
  const isFranchised = value === "Franchised";
  const color = isFranchised ? Colors.INFO : Colors.GRAY;
  return (
    <span className="bk-pill" style={{ color, background: `${color}1f` }}>
      {value || "-"}
    </span>
  );
};

/* ---------------- Mobile card ---------------- */
const MobileJeepneyCard = ({ jeepney, onView, resolveOperatorName }) => (
  <div className="bk-card mb-3">
    <div style={{ padding: 16 }}>
      <Row className="g-3">
        <Col xs={4}>
          {jeepney.image_url || jeepney.image ? (
            <Image
              src={jeepney.image_url || jeepney.image}
              alt={jeepney.license_plate}
              style={{
                width: "100%",
                height: 80,
                objectFit: "cover",
                borderRadius: T.radiusSm,
              }}
            />
          ) : (
            <div
              style={{
                width: "100%",
                height: 80,
                background: Colors.LIGHT_GRAY,
                borderRadius: T.radiusSm,
                display: "grid",
                placeItems: "center",
                color: Colors.GRAY,
              }}
            >
              <i className="bi bi-image" style={{ fontSize: "2rem" }} />
            </div>
          )}
        </Col>
        <Col xs={8}>
          <div className="d-flex justify-content-between align-items-start mb-2 gap-2">
            <div style={{ fontWeight: 700, color: Colors.TEXT_DARK }}>
              {jeepney.license_plate}
            </div>
            <StatusPill
              status={jeepney.status}
              isDisabled={jeepney.is_disabled}
            />
          </div>
          <div className="small text-muted mb-1">
            <strong>ID:</strong> {jeepney.jeepney_id}
          </div>
          <div className="small text-muted mb-1">
            <strong>Vehicle:</strong> {jeepney.vehicle_type || "-"}
          </div>
          <div className="small text-muted mb-1">
            <strong>Capacity:</strong> {jeepney.capacity}
          </div>
          <div className="small text-muted mb-1">
            <strong>Franchise:</strong> {jeepney.franchise_status || "-"}
          </div>
          <div className="small text-muted">
            <strong>Operator:</strong> {resolveOperatorName(jeepney)}
          </div>
          {jeepney.is_disabled && (
            <div
              className="small mt-2"
              style={{ color: Colors.RED, fontWeight: 600 }}
            >
              <i className="bi bi-exclamation-triangle me-1" />
              {jeepney.disabled_reason}
            </div>
          )}
        </Col>
      </Row>
      <div style={{ borderTop: T.border, marginTop: 12, paddingTop: 12 }}>
        <Button
          className="bk-btn-ghost w-100"
          onClick={() => onView(jeepney.jeepney_id)}
        >
          <i className="bi bi-eye me-1" />
          View Details
        </Button>
      </div>
    </div>
  </div>
);

/* ================================================================== */
/* MAIN COMPONENT                                                     */
/* ================================================================== */

const Jeepneys = () => {
  const nav = useNavigate();
  const abortControllerRef = useRef(null);

  /* ---------------- List state ---------------- */
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("All");
  const [compliance, setCompliance] = useState("All");
  const [loading, setLoading] = useState(false);

  /* ---------------- Detail modal ---------------- */
  const [show, setShow] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);

  /* ---------------- Operators lookup ---------------- */
  const [operators, setOperators] = useState([]);

  /* ---------------- Doc preview ---------------- */
  const [previewDoc, setPreviewDoc] = useState(null);

  /* ---------------- Responsive ---------------- */
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const h = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", h);
    return () => window.removeEventListener("resize", h);
  }, []);

  /* ================================================================ */
  /* DATA FETCHING                                                    */
  /* ================================================================ */

  const fetchRows = useCallback(
    async (signal) => {
      setLoading(true);
      try {
        const { data } = await api.get("/operator/jeepneys", {
          params: {
            page,
            per_page: PER_PAGE,
            search: search || undefined,
            status: status === "All" ? undefined : status.toLowerCase(),
            compliance:
              compliance === "All" ? undefined : compliance.toLowerCase(),
          },
          signal,
        });
        const { rows: r, total: t, last_page: lp } = parseListResponse(data);
        setRows(r);
        setPages(lp);
        setTotal(t);
      } catch (err) {
        if (err.name === "AbortError" || err.code === "ERR_CANCELED") return;
        if (err.response?.status === 401) {
          localStorage.removeItem("arkila_admin_token");
          nav("/login");
        } else {
          MySwal.fire({
            icon: "error",
            title: "Error",
            text: "Failed to load jeepneys.",
            confirmButtonColor: Colors.PRIMARY,
          });
        }
      } finally {
        setLoading(false);
      }
    },
    [page, search, status, compliance, nav],
  );

  useEffect(() => {
    if (abortControllerRef.current) abortControllerRef.current.abort();
    abortControllerRef.current = new AbortController();
    fetchRows(abortControllerRef.current.signal);
    return () => abortControllerRef.current?.abort();
  }, [fetchRows]);

  /* Fetch operator lookup once */
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/operators", {
          params: { per_page: 200 },
        });
        setOperators(parseOperatorsList(data));
      } catch (err) {
        if (err.response?.status === 401) {
          localStorage.removeItem("arkila_admin_token");
          nav("/login");
        }
      }
    })();
  }, [nav]);

  /* ================================================================ */
  /* ACTIONS                                                          */
  /* ================================================================ */

  const openModal = async (id) => {
    try {
      const { data } = await api.get(`/operator/jeepneys/${id}`);
      const jd = parseDetailResponse(data);
      if (!jd) throw new Error("Invalid response");

      setForm({
        ...EMPTY_FORM,
        jeepney_id: jd.jeepney_id,
        license_plate: jd.license_plate || "",
        operator_id: jd.operator_id || "",
        vehicle_type: jd.vehicle_type || "",
        capacity: jd.capacity || "",
        status: normalizeStatus(jd.status),
        franchise_status: jd.franchise_status || "",
        base_location: jd.base_location || "",
        has_top_load: !!jd.has_top_load,
        has_door_type: !!jd.has_door_type,
        tags: Array.isArray(jd.tags) ? jd.tags : [],
        image: jd.image || null,
        image_url: jd.image_url || jd.image || null,
        operator: jd.operator,
        documents: Array.isArray(jd.documents) ? jd.documents : [],
        is_disabled: !!jd.is_disabled || !!jd.disabled_at,
        disabled_reason: jd.disabled_reason || null,
        disabled_at: jd.disabled_at || null,
      });
      setShow(true);
    } catch (err) {
      console.error(err);
      if (err.response?.status === 401) {
        localStorage.removeItem("arkila_admin_token");
        nav("/login");
      } else {
        MySwal.fire({
          icon: "error",
          title: "Error",
          text: "Failed to load jeepney details.",
          confirmButtonColor: Colors.PRIMARY,
        });
      }
    }
  };

  const resolveOperatorName = (j) => {
    if (j.operator)
      return (
        j.operator.name ||
        `${j.operator.first_name || ""} ${j.operator.last_name || ""}`.trim()
      );
    if (!j.operator_id) return "-";
    const f = operators.find(
      (op) => String(op.operator_id) === String(j.operator_id),
    );
    if (f) return `${f.first_name || ""} ${f.last_name || ""}`.trim();
    return String(j.operator_id);
  };

  /* ================================================================ */
  /* PAGINATION                                                       */
  /* ================================================================ */

  const firstRow = (page - 1) * PER_PAGE + 1;
  const lastRow = Math.min(page * PER_PAGE, total);

  const renderPaginationItems = () => {
    const items = [];
    const max = 5;
    let start = Math.max(1, page - Math.floor(max / 2));
    const end = Math.min(pages, start + max - 1);
    if (end - start + 1 < max) start = Math.max(1, end - max + 1);
    for (let n = start; n <= end; n++) {
      items.push(
        <Pagination.Item key={n} active={n === page} onClick={() => setPage(n)}>
          {n}
        </Pagination.Item>,
      );
    }
    return items;
  };

  /* ================================================================ */
  /* LOADING SKELETON                                                 */
  /* ================================================================ */

  if (loading && rows.length === 0) {
    return (
      <div className="bk-page">
        <Styles />
        <Container fluid>
          <div className="bk-header">
            <h2>Jeepneys</h2>
            <p>Loading fleet…</p>
          </div>
          <div className="bk-card" style={{ padding: 20 }}>
            {[...Array(6)].map((_, i) => (
              <div key={i} className="bk-skel mb-3" style={{ height: 44 }} />
            ))}
          </div>
        </Container>
      </div>
    );
  }

  /* ================================================================ */
  /* MAIN RENDER                                                      */
  /* ================================================================ */

  return (
    <div className="bk-page">
      <Styles />
      <Container fluid>
        {/* ============ HEADER ============ */}
        <div className="bk-header">
          <div className="position-relative" style={{ zIndex: 1 }}>
            <h2>Jeepneys</h2>
            <p>Browse all registered jeepneys (read-only)</p>
          </div>
        </div>

        {/* ============ TOOLBAR ============ */}
        <div className="bk-toolbar">
          <Row className="g-2">
            <Col lg={6} md={5}>
              <InputGroup>
                <InputGroup.Text className="bk-search-icon">
                  <i className="bi bi-search" />
                </InputGroup.Text>
                <FormControl
                  className="bk-search"
                  placeholder="Search by license plate or vehicle type…"
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPage(1);
                  }}
                />
              </InputGroup>
            </Col>
            <Col lg={3} md={3}>
              <Form.Select
                className="bk-select"
                value={status}
                onChange={(e) => {
                  setStatus(e.target.value);
                  setPage(1);
                }}
              >
                <option value="All">All Status</option>
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
                <option value="maintenance">Maintenance</option>
              </Form.Select>
            </Col>
            <Col lg={3} md={4}>
              <Form.Select
                className="bk-select"
                value={compliance}
                onChange={(e) => {
                  setCompliance(e.target.value);
                  setPage(1);
                }}
              >
                <option value="All">All Compliance</option>
                <option value="Enabled">Enabled</option>
                <option value="Disabled">Disabled</option>
              </Form.Select>
            </Col>
          </Row>
        </div>

        {/* ============ TABLE ============ */}
        <div className="bk-card">
          <div className="bk-card-header">
            <h5>
              <i className="bi bi-truck" style={{ color: Colors.PRIMARY }} />
              Jeepney Fleet
            </h5>
            <small>
              Showing {firstRow}–{lastRow} of {total}
            </small>
          </div>

          {rows.length === 0 ? (
            <div className="bk-empty">
              <div className="bk-empty-icon">
                <i className="bi bi-truck" />
              </div>
              <h5 style={{ color: Colors.TEXT_DARK }}>No jeepneys found</h5>
              <p className="text-muted mb-0">
                {search || status !== "All" || compliance !== "All"
                  ? "No jeepneys match your filters."
                  : "No jeepneys found."}
              </p>
            </div>
          ) : (
            <>
              {/* ---- Desktop: full table ---- */}
              <div className="d-none d-lg-block">
                <Table responsive hover className="bk-table">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Image</th>
                      <th>Plate</th>
                      <th>Operator</th>
                      <th>Vehicle</th>
                      <th>Capacity</th>
                      <th>Franchise</th>
                      <th>Base Location</th>
                      <th>Status</th>
                      <th style={{ textAlign: "right" }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((j) => (
                      <tr key={j.jeepney_id}>
                        <td>
                          <span className="bk-id-chip">#{j.jeepney_id}</span>
                        </td>
                        <td>
                          {j.image_url || j.image ? (
                            <Image
                              src={j.image_url || j.image}
                              alt={j.license_plate}
                              style={{
                                width: 60,
                                height: 40,
                                objectFit: "cover",
                                borderRadius: 6,
                              }}
                            />
                          ) : (
                            <div
                              style={{
                                width: 60,
                                height: 40,
                                background: Colors.LIGHT_GRAY,
                                borderRadius: 6,
                                display: "grid",
                                placeItems: "center",
                                color: Colors.GRAY,
                              }}
                            >
                              <i className="bi bi-image" />
                            </div>
                          )}
                        </td>
                        <td style={{ fontWeight: 600 }}>{j.license_plate}</td>
                        <td>
                          <div
                            style={{
                              maxWidth: 140,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                            title={resolveOperatorName(j)}
                          >
                            {resolveOperatorName(j)}
                          </div>
                        </td>
                        <td>{j.vehicle_type || "-"}</td>
                        <td>{j.capacity}</td>
                        <td>
                          <FranchisePill value={j.franchise_status} />
                        </td>
                        <td>
                          <div
                            style={{
                              maxWidth: 140,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                            title={j.base_location || "-"}
                          >
                            {j.base_location || "-"}
                          </div>
                        </td>
                        <td>
                          <StatusPill
                            status={j.status}
                            isDisabled={j.is_disabled}
                          />
                        </td>
                        <td style={{ textAlign: "right" }}>
                          <Button
                            className="bk-btn-ghost"
                            size="sm"
                            onClick={() => openModal(j.jeepney_id)}
                          >
                            <i className="bi bi-eye me-1" />
                            View
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>

              {/* ---- Tablet: condensed table ---- */}
              <div className="d-none d-md-block d-lg-none">
                <Table responsive hover className="bk-table">
                  <thead>
                    <tr>
                      <th>Jeepney</th>
                      <th>Details</th>
                      <th>Status</th>
                      <th style={{ textAlign: "right" }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((j) => (
                      <tr key={j.jeepney_id}>
                        <td>
                          <div className="d-flex align-items-center gap-3">
                            {j.image_url || j.image ? (
                              <Image
                                src={j.image_url || j.image}
                                alt={j.license_plate}
                                style={{
                                  width: 60,
                                  height: 45,
                                  objectFit: "cover",
                                  borderRadius: 6,
                                  flexShrink: 0,
                                }}
                              />
                            ) : (
                              <div
                                style={{
                                  width: 60,
                                  height: 45,
                                  background: Colors.LIGHT_GRAY,
                                  borderRadius: 6,
                                  display: "grid",
                                  placeItems: "center",
                                  color: Colors.GRAY,
                                  flexShrink: 0,
                                }}
                              >
                                <i className="bi bi-image" />
                              </div>
                            )}
                            <div>
                              <div style={{ fontWeight: 600 }}>
                                {j.license_plate}
                              </div>
                              <small className="text-muted d-block">
                                ID: {j.jeepney_id}
                              </small>
                              <small className="text-muted">
                                {j.vehicle_type || "-"}
                              </small>
                            </div>
                          </div>
                        </td>
                        <td>
                          <div className="small">
                            <div>
                              <strong>Capacity:</strong> {j.capacity}
                            </div>
                            <div>
                              <strong>Franchise:</strong>{" "}
                              {j.franchise_status || "-"}
                            </div>
                            <div>
                              <strong>Operator:</strong>{" "}
                              {resolveOperatorName(j)}
                            </div>
                          </div>
                        </td>
                        <td>
                          <StatusPill
                            status={j.status}
                            isDisabled={j.is_disabled}
                          />
                        </td>
                        <td style={{ textAlign: "right" }}>
                          <Button
                            className="bk-btn-ghost"
                            size="sm"
                            onClick={() => openModal(j.jeepney_id)}
                          >
                            <i className="bi bi-eye me-1" />
                            View
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>

              {/* ---- Mobile: cards ---- */}
              <div className="d-md-none p-3">
                {rows.map((j) => (
                  <MobileJeepneyCard
                    key={j.jeepney_id}
                    jeepney={j}
                    onView={openModal}
                    resolveOperatorName={resolveOperatorName}
                  />
                ))}
              </div>

              {/* ---- Pagination ---- */}
              {pages > 1 && (
                <div
                  className="d-flex flex-column flex-md-row justify-content-between align-items-center gap-3"
                  style={{ padding: "14px 20px", borderTop: T.border }}
                >
                  <small className="text-muted">
                    Showing {firstRow} to {lastRow} of {total} entries
                  </small>
                  <Pagination
                    size={isMobile ? "sm" : undefined}
                    className="mb-0"
                  >
                    <Pagination.Prev
                      disabled={page === 1}
                      onClick={() => setPage(page - 1)}
                    />
                    {renderPaginationItems()}
                    <Pagination.Next
                      disabled={page === pages}
                      onClick={() => setPage(page + 1)}
                    />
                  </Pagination>
                </div>
              )}
            </>
          )}
        </div>

        {/* ============ DETAIL MODAL ============ */}
        <Modal
          show={show}
          onHide={() => setShow(false)}
          size="lg"
          centered
          fullscreen={isMobile ? "sm-down" : false}
        >
          <Modal.Header closeButton>
            <Modal.Title>
              <div className="d-flex align-items-center gap-2">
                <span
                  style={{
                    width: 34,
                    height: 34,
                    borderRadius: 10,
                    background: `${Colors.PRIMARY}1f`,
                    color: Colors.PRIMARY,
                    display: "grid",
                    placeItems: "center",
                  }}
                >
                  <i className="bi bi-truck" />
                </span>
                Jeepney Details
              </div>
            </Modal.Title>
          </Modal.Header>
          <Modal.Body
            style={{
              maxHeight: isMobile ? "calc(100vh - 120px)" : "70vh",
              overflowY: "auto",
            }}
          >
            {/* Disabled banner */}
            {form.is_disabled && (
              <div className="bk-disabled-banner">
                <div className="bk-disabled-banner-icon">
                  <i className="bi bi-exclamation-triangle-fill" />
                </div>
                <div style={{ flex: 1 }}>
                  <div className="bk-disabled-banner-title">
                    This jeepney is disabled
                  </div>
                  <p className="bk-disabled-banner-text">
                    Reason: <strong>{form.disabled_reason || "Unknown"}</strong>
                    {form.disabled_at && (
                      <>
                        {" "}
                        · Since {new Date(form.disabled_at).toLocaleString()}
                      </>
                    )}
                  </p>
                  <p
                    className="bk-disabled-banner-text"
                    style={{ marginTop: 6, opacity: 0.75 }}
                  >
                    The operator must upload valid documents to reactivate this
                    jeepney.
                  </p>
                </div>
              </div>
            )}

            <Tabs defaultActiveKey="details" className="mb-3">
              {/* ---- Details tab ---- */}
              <Tab eventKey="details" title="Details">
                <div className="bk-section">
                  <div className="bk-section-title">
                    <i className="bi bi-info-circle" /> Vehicle
                  </div>
                  <div className="bk-info-row">
                    <span className="bk-info-label">ID</span>
                    <span className="bk-info-value">#{form.jeepney_id}</span>
                  </div>
                  <div className="bk-info-row">
                    <span className="bk-info-label">License Plate</span>
                    <span className="bk-info-value">{form.license_plate}</span>
                  </div>
                  <div className="bk-info-row">
                    <span className="bk-info-label">Operator</span>
                    <span className="bk-info-value">
                      {form.operator
                        ? form.operator.name ||
                          `${form.operator.first_name || ""} ${
                            form.operator.last_name || ""
                          }`.trim()
                        : resolveOperatorName(form)}
                    </span>
                  </div>
                  <div className="bk-info-row">
                    <span className="bk-info-label">Vehicle Type</span>
                    <span className="bk-info-value">{form.vehicle_type}</span>
                  </div>
                  <div className="bk-info-row">
                    <span className="bk-info-label">Capacity</span>
                    <span className="bk-info-value">{form.capacity}</span>
                  </div>
                  <div className="bk-info-row">
                    <span className="bk-info-label">Franchise</span>
                    <span className="bk-info-value">
                      {form.franchise_status}
                    </span>
                  </div>
                  <div className="bk-info-row">
                    <span className="bk-info-label">Base Location</span>
                    <span className="bk-info-value">{form.base_location}</span>
                  </div>
                </div>
                <div className="bk-section" style={{ marginBottom: 0 }}>
                  <div className="bk-section-title">
                    <i className="bi bi-sliders" /> Features
                  </div>
                  <div className="bk-info-row">
                    <span className="bk-info-label">Status</span>
                    <StatusPill
                      status={form.status}
                      isDisabled={form.is_disabled}
                    />
                  </div>
                  <div className="bk-info-row">
                    <span className="bk-info-label">Has Top Load</span>
                    <span className="bk-info-value">
                      {form.has_top_load ? "Yes" : "No"}
                    </span>
                  </div>
                  <div className="bk-info-row">
                    <span className="bk-info-label">Has Door Type</span>
                    <span className="bk-info-value">
                      {form.has_door_type ? "Yes" : "No"}
                    </span>
                  </div>
                  <div className="bk-info-row">
                    <span className="bk-info-label">Tags</span>
                    <span className="bk-info-value">
                      {Array.isArray(form.tags) && form.tags.length
                        ? form.tags.join(", ")
                        : "-"}
                    </span>
                  </div>
                </div>
              </Tab>

              {/* ---- Image tab ---- */}
              <Tab eventKey="image" title="Image">
                <div className="text-center">
                  {form.image_url || form.image ? (
                    <Image
                      src={form.image_url || form.image}
                      alt="Jeepney"
                      style={{
                        maxWidth: "100%",
                        maxHeight: 400,
                        borderRadius: T.radiusSm,
                      }}
                    />
                  ) : (
                    <div className="text-muted py-5">
                      <i className="bi bi-image" style={{ fontSize: "3rem" }} />
                      <p className="mt-2 mb-0">No image available</p>
                    </div>
                  )}
                </div>
              </Tab>

              {/* ---- Documents tab ---- */}
              <Tab
                eventKey="documents"
                title={
                  <span>
                    Documents{" "}
                    {form.documents?.length > 0 && (
                      <span
                        className="bk-pill ms-2"
                        style={{
                          color: Colors.PRIMARY,
                          background: `${Colors.PRIMARY}1f`,
                        }}
                      >
                        {form.documents.length}
                      </span>
                    )}
                  </span>
                }
              >
                {form.documents && form.documents.length > 0 ? (
                  <>
                    {/* Desktop table */}
                    <div className="d-none d-md-block">
                      <Table responsive size="sm" hover className="bk-table">
                        <thead>
                          <tr>
                            <th>Type</th>
                            <th>Preview</th>
                            <th>Number</th>
                            <th>Issued</th>
                            <th>Expires</th>
                            <th>Status</th>
                            <th>File</th>
                          </tr>
                        </thead>
                        <tbody>
                          {form.documents.map((doc) => (
                            <tr key={doc.document_id}>
                              <td>
                                <strong>
                                  {doc.document_type_display ||
                                    doc.document_type}
                                </strong>
                              </td>
                              <td>
                                {doc.file_url ? (
                                  <img
                                    src={doc.file_url}
                                    alt={doc.document_type}
                                    className="doc-thumb"
                                    onClick={() => setPreviewDoc(doc)}
                                    onError={(e) => {
                                      e.target.style.display = "none";
                                    }}
                                  />
                                ) : (
                                  <span className="bk-no-scan">NO SCAN</span>
                                )}
                              </td>
                              <td>{doc.document_number || "-"}</td>
                              <td>{doc.formatted_issue_date || "-"}</td>
                              <td>{doc.formatted_expiry_date || "-"}</td>
                              <td>
                                <DocumentBadge doc={doc} />
                              </td>
                              <td>
                                {doc.file_url ? (
                                  <Button
                                    className="bk-btn-ghost"
                                    size="sm"
                                    onClick={() => setPreviewDoc(doc)}
                                  >
                                    <i className="bi bi-eye me-1" />
                                    View
                                  </Button>
                                ) : (
                                  <span className="text-muted">-</span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>

                    {/* Mobile cards */}
                    <div className="d-md-none">
                      {form.documents.map((doc) => (
                        <div key={doc.document_id} className="bk-section">
                          <div className="d-flex justify-content-between align-items-start mb-2 gap-2">
                            <strong style={{ color: Colors.TEXT_DARK }}>
                              {doc.document_type_display || doc.document_type}
                            </strong>
                            <DocumentBadge doc={doc} />
                          </div>
                          {doc.file_url ? (
                            <img
                              src={doc.file_url}
                              alt={doc.document_type}
                              style={{
                                width: "100%",
                                borderRadius: T.radiusSm,
                                marginBottom: 12,
                                cursor: "pointer",
                              }}
                              onClick={() => setPreviewDoc(doc)}
                            />
                          ) : (
                            <div
                              style={{
                                width: "100%",
                                padding: "20px 0",
                                textAlign: "center",
                                background: Colors.LIGHT_GRAY,
                                borderRadius: T.radiusSm,
                                marginBottom: 12,
                                color: Colors.TEXT_LIGHT,
                                fontSize: 12,
                                fontWeight: 700,
                                letterSpacing: ".05em",
                                border: `1px dashed ${Colors.BORDER_GRAY}`,
                              }}
                            >
                              NO SCAN ATTACHED
                            </div>
                          )}
                          <div className="small text-muted mb-1">
                            <strong>Number:</strong>{" "}
                            {doc.document_number || "-"}
                          </div>
                          <div className="small text-muted mb-1">
                            <strong>Issued:</strong>{" "}
                            {doc.formatted_issue_date || "-"}
                          </div>
                          <div className="small text-muted mb-2">
                            <strong>Expires:</strong>{" "}
                            {doc.formatted_expiry_date || "-"}
                          </div>
                          {doc.file_url && (
                            <Button
                              className="bk-btn-ghost w-100"
                              onClick={() => setPreviewDoc(doc)}
                            >
                              <i className="bi bi-eye me-1" />
                              View Document
                            </Button>
                          )}
                        </div>
                      ))}
                    </div>
                  </>
                ) : (
                  <div className="text-muted text-center py-5">
                    <i
                      className="bi bi-file-earmark-x"
                      style={{ fontSize: "3rem" }}
                    />
                    <p className="mt-2 mb-0">No documents on file</p>
                    <small>
                      The operator has not uploaded any documents for this
                      jeepney yet.
                    </small>
                  </div>
                )}
              </Tab>
            </Tabs>
          </Modal.Body>
          <Modal.Footer>
            <Button className="bk-btn-ghost" onClick={() => setShow(false)}>
              Close
            </Button>
          </Modal.Footer>
        </Modal>

        {/* ============ DOCUMENT PREVIEW MODAL ============ */}
        <Modal
          show={!!previewDoc}
          onHide={() => setPreviewDoc(null)}
          size="lg"
          centered
          fullscreen={isMobile ? "sm-down" : false}
          contentClassName="border-0"
        >
          <Modal.Header
            closeButton
            style={{
              background: "#1a1a1a",
              color: "#fff",
              borderBottom: "none",
            }}
          >
            <Modal.Title style={{ color: "#fff", fontSize: 15 }}>
              <div className="d-flex align-items-center gap-2">
                <span
                  style={{
                    width: 34,
                    height: 34,
                    borderRadius: 10,
                    background: `${Colors.PRIMARY}33`,
                    color: Colors.PRIMARY,
                    display: "grid",
                    placeItems: "center",
                  }}
                >
                  <i className="bi bi-file-earmark-image" />
                </span>
                <div>
                  <div>
                    {previewDoc?.document_type_display ||
                      previewDoc?.document_type ||
                      "Document"}
                  </div>
                  {previewDoc?.document_number && (
                    <div
                      style={{
                        fontSize: 12,
                        opacity: 0.7,
                        fontWeight: 400,
                      }}
                    >
                      Doc No: {previewDoc.document_number}
                    </div>
                  )}
                </div>
              </div>
            </Modal.Title>
          </Modal.Header>
          <Modal.Body
            className="doc-preview-body"
            style={{ minHeight: 300, maxHeight: "75vh", overflow: "auto" }}
          >
            {previewDoc?.file_url ? (
              <img
                src={previewDoc.file_url}
                alt={previewDoc.document_type_display || "Document"}
                className="doc-preview-img"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = "";
                  e.target.style.display = "none";
                  e.target.parentNode.insertAdjacentHTML(
                    "beforeend",
                    '<div style="color:#888;padding:40px;text-align:center"><i class="bi bi-exclamation-triangle" style="font-size:2.5rem"></i><p style="margin-top:12px">Failed to load document image</p></div>',
                  );
                }}
              />
            ) : (
              <div style={{ color: "#888", padding: 40, textAlign: "center" }}>
                <i className="bi bi-image" style={{ fontSize: "3rem" }} />
                <p className="mt-2 mb-0">No file attached</p>
              </div>
            )}
          </Modal.Body>
          <Modal.Footer
            style={{
              background: "#1a1a1a",
              borderTop: "none",
              justifyContent: "space-between",
            }}
          >
            <div style={{ color: "#fff", opacity: 0.6, fontSize: 12 }}>
              {previewDoc?.formatted_expiry_date &&
                previewDoc.formatted_expiry_date !== "N/A" && (
                  <>Expires: {previewDoc.formatted_expiry_date}</>
                )}
            </div>
            <div className="d-flex gap-2">
              {previewDoc?.file_url && (
                <a
                  href={previewDoc.file_url}
                  download={`${previewDoc.document_type || "document"}_${
                    previewDoc.document_number || ""
                  }.jpg`}
                  className="bk-btn-ghost"
                  style={{
                    textDecoration: "none",
                    background: "transparent",
                    color: "#fff",
                    borderColor: "#444",
                  }}
                >
                  <i className="bi bi-download me-1" />
                  Download
                </a>
              )}
              <Button
                className="bk-btn-primary"
                onClick={() => setPreviewDoc(null)}
              >
                Close
              </Button>
            </div>
          </Modal.Footer>
        </Modal>
      </Container>
    </div>
  );
};

export default Jeepneys;
